import { ErrorHandlerService } from '@loavies/utils'
import {
  UpdateUserModel,
  UpdateUserPrivateDataModel,
  UpdateUserPublicDataModel,
} from '@loavies/c2c-models'

import { MarketplaceImageService } from './marketplace-image-service'

import { mapUser } from '../response-mappers/user-mappers'
import { mapRelations } from '../response-mappers/relations-mapper'

export class MarketplaceUserService {
  // TODO: add JSDocs for references to the services:
  // https://loavies.atlassian.net/browse/C2C-133
  constructor(marketplaceService) {
    this.marketplaceService = marketplaceService
    this.marketplace = marketplaceService.instance
    this.errorHandlerService = new ErrorHandlerService()
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#create-user
   * @param {loavies.models.user.CreateMarketplaceUserModel} createMarketplaceUserModel
   * @return {Promise<loavies.models.user.UserModel>}
   */
  createUser(createMarketplaceUserModel) {
    return this.marketplace.currentUser.create(createMarketplaceUserModel, {
      expand: true,
    })
      .then(response => mapRelations(response.data))
      .then(response => mapUser(response.data))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#update-user-profile
   * @param {loavies.models.user.UpdateUserModel} updateUserModel
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateUser(updateUserModel) {
    return this.marketplace.currentUser.updateProfile(updateUserModel, {
      expand: true,
      include: ['profileImage'],
      ...this.marketplaceService.getImageVariants(),
    })
      .then(response => mapRelations(response.data))
      .then(response => mapUser(response.data))
  }

  /**
   * @param {boolean} chatBrowserNotificationsEnabled
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateChatBrowserNotificationsEnabled(chatBrowserNotificationsEnabled) {
    return this.updateUser(new UpdateUserModel({
      privateData: new UpdateUserPrivateDataModel({
        chatBrowserNotificationsEnabled: chatBrowserNotificationsEnabled,
      }),
    }))
  }

  /**
   * @param {loavies.models.user.UserEditFormModel} userEditFormModel
   * @return {Promise<loavies.models.user.UserModel>}
   */
  async updateEditableUserData(userEditFormModel) {
    let profileImageId

    if (userEditFormModel.avatar) {
      try {
        profileImageId = await new MarketplaceImageService(this.marketplaceService).uploadImage(userEditFormModel.avatar)
      } catch (error) {
        return Promise.reject(error)
      }
    }

    return this.updateUser(new UpdateUserModel({
      ...userEditFormModel,
      profileImageId,
    }))
  }

  /**
   * @param {string} newUsername
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateDisplayName(newUsername) {
    return this.updateUser(new UpdateUserModel({
      displayName: newUsername,
    }))
  }

  /**
   * @param {string} newBio
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateBio(newBio) {
    return this.updateUser(new UpdateUserModel({
      bio: newBio,
    }))
  }

  /**
   * @param {string} newStoreName
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateStoreName(newStoreName) {
    return this.updateUser(new UpdateUserModel({
      publicData: new UpdateUserPublicDataModel({
        storeName: newStoreName,
      }),
    }))
  }

  /**
   * @param {File} newAvatar
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateAvatar(newAvatar) {
    return new MarketplaceImageService(this.marketplaceService).uploadImage(newAvatar)
      .then(profileImageId => this.updateUser(new UpdateUserModel({ profileImageId })))
  }

  /**
   * @param {string} newBannerSrc
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateBanner(newBannerSrc) {
    return this.updateUser(new UpdateUserModel({
      publicData: new UpdateUserPublicDataModel({
        banner: newBannerSrc,
      }),
    }))
  }

  /**
   * @param {boolean} [isEnabled]
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateAccountOnboardingEnabled(isEnabled = false) {
    return this.updateUser(new UpdateUserModel({
      privateData: new UpdateUserPrivateDataModel({
        accountOnboardingEnabled: isEnabled,
      }),
    }))
  }

  /**
   * @param {boolean} [isEnabled]
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateGeneralOnboardingEnabled(isEnabled = false) {
    return this.updateUser(new UpdateUserModel({
      privateData: new UpdateUserPrivateDataModel({
        generalOnboardingEnabled: isEnabled,
      }),
    }))
  }

  /**
   * @param {string} newGridType
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateStoreGridType(newGridType) {
    return this.updateUser(new UpdateUserModel({
      publicData: new UpdateUserPublicDataModel({
        storeGridType: newGridType,
      }),
    }))
  }

  /**
   * @param {string} [lastEventSequenceId] number
   * @return {Promise<loavies.models.user.UserModel>}
   */
  updateLastEventSequenceId(lastEventSequenceId) {
    return this.updateUser(new UpdateUserModel({
      privateData: new UpdateUserPrivateDataModel({
        lastEventSequenceId,
      }),
    }))
  }

  /**
   * @param {string[]} wishlistIds
   * @return {Promise<loavies.models.user.UserModel>}
   */
  setWishList(wishlistIds) {
    return this.updateUser(new UpdateUserModel({
      privateData: new UpdateUserPrivateDataModel({
        wishlist: wishlistIds,
      }),
    }))
  }

  /**
   * @param {string[]} followingUserIds
   * @return {Promise<loavies.models.user.UserModel>}
   */
  setFollowingUsers(followingUserIds) {
    return this.updateUser(new UpdateUserModel({
      publicData: new UpdateUserPublicDataModel({
        followingUsers: followingUserIds,
      }),
    }))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#change-password
   * @param {loavies.models.user.UpdatePasswordModel} updatePasswordModel
   * @return {Promise}
   */
  changePassword(updatePasswordModel) {
    return this.marketplace.currentUser.updatePassword(updatePasswordModel)
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#change-email-address
   * @param {string} currentPassword
   * @param {string} email
   * @return {Promise}
   */
  changeEmail(currentPassword, email) {
    return this.marketplace.currentUser.changeEmail({
      currentPassword,
      email,
    })
  }

  /**
   * @param {number} sequenceId
   * @return {Promise<loavies.models.user.UserModel>}
   */
  setLastEventSequenceIdNotificationShown(sequenceId) {
    return this.updateUser(new UpdateUserModel({
      privateData: new UpdateUserPrivateDataModel({
        lastEventSequenceIdNotificationShown: sequenceId,
      }),
    }))
  }

  /**
   * @param {number} sequenceId
   * @return {Promise<loavies.models.user.UserModel>}
   */
  setLastEventSequenceIdMessageRead(sequenceId) {
    return this.updateUser(new UpdateUserModel({
      privateData: new UpdateUserPrivateDataModel({
        lastEventSequenceIdMessageRead: sequenceId,
        lastEventSequenceIdNotificationShown: sequenceId,
      }),
    }))
  }

  /**
   * @param {boolean} storeIsClosed
   * @return {Promise<loavies.models.user.UserModel>}
   */
  setStoreIsClosed(storeIsClosed) {
    return this.updateUser(new UpdateUserModel({
      publicData: new UpdateUserPublicDataModel({
        storeIsClosed,
      }),
    }))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#show-current-user
   * @return {Promise<loavies.models.user.UserModel>}
   */
  getCurrentUser() {
    return this.marketplace.currentUser.show({ include: ['profileImage'], ...this.marketplaceService.getImageVariants() })
      .then(response => mapRelations(response.data))
      .then(response => mapUser(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#show-user
   * @params {string} id
   * @return {Promise<loavies.models.user.UserModel>}
   */
  getUserById(id) {
    return this.marketplace.users.show({
      id,
      include: ['profileImage'],
      ...this.marketplaceService.getImageVariants(),
    })
      .then(response => mapRelations(response.data))
      .then(response => mapUser(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }
}
